import { Stack } from '@mui/material';
import * as refine from '@refinedev/core';
import { Create, Edit, useAutocomplete } from '@refinedev/mui';
import { useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import type { Category } from 'shiver-core/prisma/client';
import { BaseField } from '../../../components/fields/BaseField.tsx';
import { SelectField } from '../../../components/fields/SelectField.tsx';
import { Sector } from '../../../types/index.ts';

const COMPONENT_MAP = { create: Create, edit: Edit } as const;

export const SectorForm = ({ action }: { action: 'create' | 'edit' }) => {
  const { query, formLoading, onFinish } = refine.useForm<Sector>({
    resource: 'contents/sectors',
    action,
  });

  const data = useMemo(
    () => (query?.data?.data ? Sector.parse(query?.data?.data) : undefined),
    [query?.data?.data],
  );

  const methods = useForm<Sector>({
    values: data,
  });

  const Component = COMPONENT_MAP[action];

  const { autocompleteProps: categoryAutocompleteProps } = useAutocomplete<Category>({
    resource: 'contents/categories',
    filters: [],
    sort: [{ field: 'name', order: 'asc' }],
    onSearch: (value) => [{ field: 'name', operator: 'contains', value }],
  });

  return (
    <Component
      canDelete
      isLoading={formLoading}
      saveButtonProps={{
        disabled: methods.formState.isSubmitting || formLoading,
        onClick: methods.handleSubmit((data) => {
          const processedData = {
            name: data.name,
            category: data.category,
          };

          return onFinish(processedData);
        }),
      }}
    >
      <FormProvider {...methods}>
        <Stack spacing={2}>
          <BaseField
            name="name"
            rules={{
              required: '必須',
            }}
            label="名称"
            fullWidth
          />
          <SelectField
            {...categoryAutocompleteProps}
            name="category"
            label="カテゴリ"
            rules={{
              required: '必須',
            }}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disableClearable
            sx={{ flex: 1 }}
          />
        </Stack>
      </FormProvider>
    </Component>
  );
};
